<template>
  <Stack>
    <Box
      v-for="issueGroup in groupedScreens"
      :key="issueGroup.issue"
      :class="`group is-${issueGroup.slug}`"
    >
      <header v-if="issueGroup.name" class="header">
        <Title class="name" tag="h3" size="4">
          <Icon :icon="`fas ${issueGroup.icon}`" />
          {{ issueGroup.name }}
        </Title>
      </header>

      <Stack class="main" gap="2">
        <p class="counts">
          {{ issueGroup.subgroupCount }} categories, {{ issueGroup.screenCount }} issues
        </p>

        <Stack
          v-for="subGroup in issueGroup.subcategories"
          :key="subGroup.name"
          gap="1"
          :class="['subcategories', { 'is-collapsed': collapsedGroups.has(subGroup.name) }]"
        >
          <button class="toggle stk-reset" @click="toggleGroup(subGroup.name)">
            <Icon class="toggle-icon" icon="fas fa-chevron-down" size="3_5" />

            <Title tag="h4" size="4_5">
              {{ subGroup.name }}
            </Title>

            <span class="subgroup-count">{{ subGroup.screens.length }}</span>
          </button>

          <Stack class="screens" gap="1">
            <div
              v-for="screen in subGroup.screens"
              :key="screen.id"
              :mode="mode"
              class="screen"
              :class="{
                'is-active': activeScreen && screen.id === activeScreen.id,
                'is-applied': screen.userPercentile > 0,
                'not-applicable': applicableScreens && !(screen.id in applicableScreens),
              }"
            >
              <div class="screen-meta">
                <b-tooltip :label="screenTypeInfo(screen)">
                  <Icon class="screen-icon" :icon="`fas ${screenTypeIcon(screen)}`" />
                </b-tooltip>
              </div>

              <TextLink @click="$emit('click', screen)" variant="tight">
                {{ screen.name }}
              </TextLink>

              <StakeTooltip
                v-if="screen.metric_meter_weighting && screen.metric_meter_weighting.base >= 8"
                :label="DIFFICULT_METRICS.iconTooltip"
                class="difficult-metric-icon"
              >
                <i class="fas fa-dumbbell"></i>
              </StakeTooltip>

              <div
                v-if="(mode === 'screening' || mode === 'selecting') && !screen.is_binary && screen.manuallySet"
                class="screen-slider"
              >
                <ScreenSlider
                  v-bind:screen="screen"
                  v-on:change="updatePercentile(screen, $event)"
                />
              </div>

              <div
                v-if="mode === 'hml' && !screen.is_binary"
                class="screen-slider"
              >
                <ScreenSliderHML
                  v-bind:screen="screen"
                  v-on:change="updatePercentile(screen, $event)"
                />
              </div>

              <div
                v-if="mode === 'selecting' || screen.is_binary || (mode === 'screening' && !screen.manuallySet)"
                class="screen-switch"
              >
                <ScreenSwitch
                  v-bind:screen="screen"
                  v-on:change="updatePercentile(screen, $event)"
                />
              </div>

              <Badge v-if="applicableScreens && !(screen.id in applicableScreens)" class="not-applicable-badge">
                Not applicable
              </Badge>
            </div>
          </Stack>
        </Stack>
      </Stack>
    </Box>
  </Stack>
</template>

<script>
import { Badge, TextLink, Icon, Stack, Title, Box } from '@stakeui';
import StakeTooltip from '@stakeui/StakeTooltip.vue';

import ScreenSwitch from '@frontend/builder/js/components/ScreenSwitch.vue';
import ScreenSlider from '@frontend/builder/js/components/ScreenSlider.vue';
import ScreenSliderHML from '@frontend/builder/js/components/ScreenSliderHML.vue';

import eventBus from '@frontend/shared/EventBus.js';
import { DIFFICULT_METRICS } from '@frontend/dash3/js/components/exclusionInclusionMessages.js';

export default {
  components: { Badge, TextLink, Icon, Stack, Title, Box, ScreenSlider, ScreenSwitch, ScreenSliderHML, StakeTooltip },
  data() {
    return {
      collapsedGroups: new Set(),
      DIFFICULT_METRICS,
    };
  },
  props: {
    screens: {
      type: Array,
      required: true,
      default: () => [],
    },
    activeScreen: {
      type: Object,
      default: () => {},
    },
    applicableScreens: {
      type: Object,
      default: () => {},
    },
    mode: {
      type: String,
      default: 'screening',
      validator: function(value) {
        return ['screening', 'selecting', 'hml'].indexOf(value) !== -1;
      },
    },
  },
  computed: {
    groupedScreens() {
      const issueMap = {};

      this.screens.filter(screen => screen.subcategory?.length > 0).forEach(screen => {
        const issue = screen.subcategory[0].esgissue;
        const sub = screen.subcategory[0];

        if (!issueMap[issue.name]) {
          issueMap[issue.name] = { name: issue.name, slug: issue.slug, icon: issue.icon, subcategories: {} };
        }

        if (!issueMap[issue.name].subcategories[sub.name]) {
          issueMap[issue.name].subcategories[sub.name] = { name: sub.name, image: sub.image, screens: [] };
        }

        issueMap[issue.name].subcategories[sub.name].screens.push(screen);
      });

      return Object.values(issueMap).map(issue => {
        const subcategories = Object.values(issue.subcategories);
        return {
          ...issue,
          subcategories,
          subgroupCount: subcategories.length,
          screenCount: subcategories.reduce((sum, sub) => sum + sub.screens.length, 0),
        };
      });
    },
  },
  methods: {
    updatePercentile(screen, newValue) {
      screen.userPercentile = newValue;
      eventBus.$emit('screens-changed', screen);
    },
    toggleGroup(name) {
      if (this.collapsedGroups.has(name)) {
        this.collapsedGroups.delete(name);
      } else {
        this.collapsedGroups.add(name);
      }
      this.collapsedGroups = new Set(this.collapsedGroups);
    },
    screenTypeIcon(screen) {
      if (screen.is_binary && screen.is_inclusionary) {
        return 'fa-plus-circle';
      }
      if (screen.is_binary && !screen.is_inclusionary) {
        return 'fa-minus-circle';
      }
      return 'fa-sliders-h';
    },
    screenTypeInfo(screen) {
      if (screen.is_binary && screen.is_inclusionary) {
        return 'Positive Binary Screen';
      }
      if (screen.is_binary && !screen.is_inclusionary) {
        return 'Negative Binary Screen';
      }
      return 'Threshold Screen';
    },
  },
};
</script>

<style scoped>
.group {
  padding: 0;
  border: none;
  gap: 0;

  &.is-environment {
    --bg: var(--stk-environment-90);
    --bg-light: var(--stk-environment-95);
    --fg: var(--stk-environment-40);
  }

  &.is-equal-opportunity {
    --bg: var(--stk-equal-opportunity-90);
    --bg-light: var(--stk-equal-opportunity-95);
    --fg: var(--stk-equal-opportunity-50);
  }

  &.is-health {
    --bg: var(--stk-health-90);
    --bg-light: var(--stk-health-95);
    --fg: var(--stk-health-50);
  }

  &.is-human-rights {
    --bg: var(--stk-human-rights-90);
    --bg-light: var(--stk-human-rights-95);
    --fg: var(--stk-human-rights-40);
  }

  &.is-accountability {
    --bg: var(--stk-accountability-95);
    --bg-light: var(--stk-accountability-95);
    --fg: var(--stk-accountability-50);
  }
}

.header {
  background-color: var(--bg);
  padding: 1.25em;
  border-radius: 1em 1em 0 0;
}

.name {
  color: var(--fg);
  font-size: 1.25em;
}

.main {
  padding: 1.25em;
  border: 1px solid var(--stk-border);
  border-top: none;
  border-radius: 0 0 1em 1em;
}

.counts {
  color: var(--stk-text-weak);
}

.toggle {
  display: flex;
  align-items: center;
  color: var(--stk-text-weak);
  gap: 0.55em;
  padding: 0.5em;
  background-color: var(--stk-background);
  border-radius: 0.5em;
  cursor: pointer;
  transition-duration: var(--stk-transition-duration);
  transition-property: background-color, color;

  :deep(.stk-icon) {
    color: inherit;
    transition-duration: var(--stk-transition-duration);
    transition-property: color;
  }

  &:hover {
    background-color: var(--stk-background-alt);
    color: var(--fg);
  }

  &:active {
    background-color: var(--stk-background-bis);
    color: var(--fg);
  }
}

.toggle-icon {
  transition-duration: var(--stk-transition-duration);
  transition-property: transform;
}

.screens {
  padding: 0.5em 0 0.5em 2.25em;
}

.subcategories.is-collapsed {
  .toggle-icon {
    transform: rotate(-90deg);
  }

  .screens {
    display: none;
  }
}

.subgroup-count {
  margin-left: auto;
  font-size: 0.75em;
  color: var(--stk-text-weak);
}

.screen {
  display: flex;
  align-items: center;
  gap: 0.5em;
  border-radius: 0.5em;
  padding: 0.25em 0.5em;
  transition-duration: var(--stk-transition-duration);
  transition-property: background-color;
  position: relative;

  &:hover {
    background-color: var(--bg-light);
    color: var(--fg);

    .screen-slider {
      overflow: visible;
    }
  }

  &.is-active {
    background-color: var(--bg);
  }

  &.is-applied {
    background-color: var(--fg);
    color: #fff;
  }

  &.not-applicable {
    pointer-events: none;

    > * {
      opacity: 0.5;
    }

    > .not-applicable-badge {
      opacity: 1;
    }
  }

  :deep(.tooltip-trigger) {
    display: flex;
  }
}

.not-applicable-badge {
  position: absolute;
  right: 0.5em;
}

.screen-icon {
  opacity: 0.3;
}

.screen-slider {
  width: 120px;
  flex-shrink: 0;
  display: flex;
  margin-left: auto;
  overflow: hidden;
  height: 24px;
  padding: 0 12px;

  :deep(.b-slider) {
    margin-top: 8px;
  }
}

.screen-switch {
  width: 60px;
  flex-shrink: 0;
  display: flex;
  margin-left: auto;
  justify-content: end;

  > div {
    display: flex;
    align-items: center;
  }

  :deep(.switch) {
    top: auto;
    left: auto;
    font-size: 0.75rem;
  }

  :deep(.switch input[type=checkbox]:checked + .check) {
    background-color: var(--bg) !important;
  }

  :deep(.switch input[type=checkbox]:checked + .check:before) {
    background-color: var(--fg) !important;
  }
}

.screen-meta {
  display: flex;
  align-items: center;
}
</style>
